import React, { useState } from 'react';
import {
    Button,
    Card,
    Badge,
    Offcanvas,
    Form,
    InputGroup,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import './ChatWidget.css';

/**
 * ChatWidget component for AI assistant integration
 * Displays a floating chat button that opens a chat panel
 *
 * @param {Object} props
 * @param {string} props.persona - 'piper' for faculty or 'rowan' for students
 * @param {boolean} props.comingSoon - Show "Coming Soon" placeholder instead of functional chat
 */
function ChatWidget({ persona = 'rowan', comingSoon = true }) {
    const [show, setShow] = useState(false);
    const [message, setMessage] = useState('');

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const personaConfig = {
        piper: {
            name: 'Piper',
            description: 'Your Faculty Support Assistant',
            color: '#1565C0', // TA blue
            greeting:
                "Hello! I'm Piper, your faculty support assistant. I can help you navigate the peer support request process, answer questions about program requirements, and guide you through the system.",
            placeholder: 'Ask Piper about TA requests, course support...',
        },
        rowan: {
            name: 'Rowan',
            description: 'Your Student Support Assistant',
            color: '#2E7D32', // SI green
            greeting:
                "Hi there! I'm Rowan, your student support assistant. I can help you understand program requirements, guide you through the application process, and answer questions about being a peer supporter.",
            placeholder: 'Ask Rowan about applying, programs...',
        },
    };

    const config = personaConfig[persona] || personaConfig.rowan;

    const handleSubmit = (e) => {
        e.preventDefault();
        setMessage('');
    };

    return (
        <>
            {/* Floating Chat Button */}
            <Button
                className="chat-widget-button"
                onClick={handleShow}
                style={{
                    backgroundColor: config.color,
                    borderColor: config.color,
                }}
                aria-label={`Open chat with ${config.name}`}
            >
                <span className="chat-widget-icon">💬</span>
                <span className="chat-widget-label">{config.name}</span>
            </Button>

            {/* Chat Panel */}
            <Offcanvas
                show={show}
                onHide={handleClose}
                placement="end"
                className="chat-widget-panel"
            >
                <Offcanvas.Header
                    closeButton
                    style={{ backgroundColor: config.color, color: 'white' }}
                >
                    <Offcanvas.Title>
                        <div className="d-flex align-items-center">
                            <span className="me-2">{config.name}</span>
                            {comingSoon && (
                                <Badge
                                    bg="warning"
                                    text="dark"
                                    className="ms-2"
                                >
                                    Coming Soon
                                </Badge>
                            )}
                        </div>
                        <small className="d-block chat-widget-description">
                            {config.description}
                        </small>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="d-flex flex-column">
                    {comingSoon ? (
                        /* Coming Soon Placeholder */
                        <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center text-center p-4">
                            <div className="display-1 mb-3">🤖</div>
                            <h4 className="fw-bold mb-3">Meet {config.name}</h4>
                            <p className="text-muted mb-4">{config.greeting}</p>
                            <Card className="bg-light border-0 w-100">
                                <Card.Body>
                                    <h6 className="fw-bold mb-2">
                                        Coming Soon!
                                    </h6>
                                    <p className="small text-muted mb-0">
                                        {config.name} is being trained to help
                                        you with the Peer Support Hub. Check
                                        back soon for AI-powered assistance!
                                    </p>
                                </Card.Body>
                            </Card>
                            <div className="mt-4">
                                <h6 className="text-muted mb-2">
                                    {config.name} will help you:
                                </h6>
                                {persona === 'piper' ? (
                                    <ul className="text-start small text-muted">
                                        <li>
                                            Request Teaching Assistants for your
                                            courses
                                        </li>
                                        <li>
                                            Understand program requirements and
                                            timelines
                                        </li>
                                        <li>
                                            View applications for your courses
                                        </li>
                                        <li>
                                            Navigate the coordinator dashboard
                                        </li>
                                    </ul>
                                ) : (
                                    <ul className="text-start small text-muted">
                                        <li>
                                            Find courses seeking peer support
                                        </li>
                                        <li>
                                            Understand LA, SI, and TA
                                            requirements
                                        </li>
                                        <li>Complete your application</li>
                                        <li>Track your application status</li>
                                    </ul>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* Future: Active Chat Interface */
                        <>
                            <div className="chat-messages flex-grow-1 overflow-auto mb-3">
                                {/* Chat messages will go here */}
                                <div className="chat-message assistant">
                                    <div className="chat-bubble">
                                        {config.greeting}
                                    </div>
                                </div>
                            </div>
                            <Form onSubmit={handleSubmit}>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder={config.placeholder}
                                        value={message}
                                        onChange={(e) =>
                                            setMessage(e.target.value)
                                        }
                                    />
                                    <Button
                                        type="submit"
                                        style={{
                                            backgroundColor: config.color,
                                            borderColor: config.color,
                                        }}
                                        disabled={!message.trim()}
                                    >
                                        Send
                                    </Button>
                                </InputGroup>
                            </Form>
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

ChatWidget.propTypes = {
    persona: PropTypes.oneOf(['piper', 'rowan']),
    comingSoon: PropTypes.bool,
};

export default ChatWidget;
